import './Projects.css';

import projects from "./data/projects.json";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, Box, Typography, Stack, Button, Portal } from "@mui/material";
import { Code as CodeIcon, GitHub, PlayCircle, Public } from "@mui/icons-material";
import { BsChevronDoubleUp } from "react-icons/bs";
import LazyImage from "./LazyImage";

import { motion, AnimatePresence } from "framer-motion";

function LazyThumb({ src, alt, sx }) {
    return (
        <LazyImage
            src={src}
            alt={alt}
            wrapperSx={{
                height: 200,
                width: "100%",
                ...sx
            }}
            spinnerSx={{
                "&::before": {
                    content: '""',
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "common.white",
                    boxShadow: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    position: "absolute"
                }
            }}
            sx={{
                maxHeight: "85%",
                maxWidth: "100%",
                objectFit: "contain",
                borderRadius: 2,
                zIndex: 2
            }}
        />
    );
}

const clamp = {
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
};

const categoryPages = [
    { key: "all", title: "All" },
    { key: "websites", title: "Websites" },
    { key: "modules", title: "Modules" },
    { key: "other", title: "Other" }
];

const labelBaseSx = {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    px: 1,
    borderRadius: 999,
    fontSize: 12,
    lineHeight: 1,
    whiteSpace: "nowrap",
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
    color: "text.primary"
};

function Label({ children, tone = "default", sx }) {
    const toneSx = {
        default: {},
        live: { bgcolor: "#2e7d32", borderColor: "#2e7d32", color: "#fff", fontWeight: 700 },
        warning: { borderColor: "#ff9800", color: "#e65100", fontWeight: 700 },
        code: { bgcolor: "#8e24aa", borderColor: "#8e24aa", color: "#fff", fontWeight: 700 },
        muted: { color: "text.secondary" },
        light: { bgcolor: "#faf9f1", borderColor: "rgba(255,255,255,0.45)" }
    };

    return <Box component="span" sx={{ ...labelBaseSx, ...toneSx[tone], ...sx }}>{children}</Box>;
}

function FilterOption({ selected, children, onClick, selectedTone = "blue" }) {
    const selectedYellow = selected && selectedTone === "yellow";

    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            sx={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: 34,
                px: 1.4,
                py: 0.75,
                border: "1px solid transparent",
                borderLeft: selected ? "4px solid #f1c40f" : "4px solid transparent",
                borderRadius: 1.5,
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.25,
                fontWeight: selected ? 800 : 500,
                bgcolor: selectedYellow ? "#f1c40f" : selected ? "#061c35" : "#0e2b4d",
                borderColor: selectedYellow ? "#f1c40f" : selected ? "#061c35" : "rgba(14,43,77,0.22)",
                color: selectedYellow ? "#0e2b4d" : "#fff",
                boxShadow: "0 1px 2px rgba(14,43,77,0.18)",
                transition: "background-color .2s ease, color .2s ease, transform .2s ease, box-shadow .2s ease",
                "&:hover": {
                    transform: "translateX(2px)",
                    bgcolor: selectedYellow ? "#ffd84d" : "#123d69",
                    boxShadow: "0 2px 6px rgba(14,43,77,0.2)"
                },
                "&:focus-visible": {
                    outline: "2px solid #f1c40f",
                    outlineOffset: 2
                }
            }}
        >
            <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{children}</Box>
        </Box>
    );
}

const technologyGroups = [
    { title: "Frameworks / Libraries", values: ["React", "Angular 13", "Redux", "Vite"] },
    { title: "Languages", values: ["JavaScript", "TypeScript", "HTML", "CSS"] },
    { title: "Styling", values: ["Tailwind CSS"] },
    { title: "Backend", values: ["Node.js"] },
    { title: "Databases", values: ["MySQL", "MongoDB"] },
    { title: "Payments", values: ["Stripe"] }
];

const statusFilterValues = ["Live", "Video", "Code"];

function StatusLabel({ status }) {
    const iconSx = { fontSize: 16, flex: "0 0 auto" };
    const icons = {
        Live: <Public sx={iconSx} />,
        Video: <PlayCircle sx={iconSx} />,
        Code: <CodeIcon sx={iconSx} />
    };

    return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.65, minWidth: 0 }}>
            {icons[status]}
            <Box component="span">{status}</Box>
        </Box>
    );
}

function getTechnologySubgroups(options) {
    const remaining = new Set(options);
    const groups = technologyGroups
        .map((group) => {
            const values = group.values.filter((value) => remaining.has(value));
            values.forEach((value) => remaining.delete(value));
            return { title: group.title, options: values };
        })
        .filter((group) => group.options.length > 0);

    if (remaining.size > 0) {
        groups.push({ title: "Other", options: Array.from(remaining) });
    }

    return groups;
}

function getProjectTechLabels(project) {
    const labels = [];
    const addLabel = (tech) => {
        const label = tech === "TS" ? "TypeScript" : tech;
        if (label && !labels.includes(label)) {
            labels.push(label);
        }
    };

    project.stack.forEach((tech) => {
        if (tech.includes("/")) {
            tech.split("/").forEach((part) => {
                addLabel(part.trim());
            });
            return;
        }

        addLabel(tech);
    });

    if (labels.includes("Vite") || labels.includes("Angular 13")) {
        addLabel("TypeScript");
    }

    if (labels.includes("React") && !labels.includes("TypeScript")) {
        addLabel("JavaScript");
    }

    return labels;
}

function AnimatedDropdown({ title, children, nested = false, selectedCount = 0 }) {
    const [open, setOpen] = useState(selectedCount > 0);
    const previousSelectedCountRef = useRef(selectedCount);
    const hasSelectedFilters = selectedCount > 0;

    useEffect(() => {
        if (selectedCount > previousSelectedCountRef.current) {
            setOpen(true);
        }

        previousSelectedCountRef.current = selectedCount;
    }, [selectedCount]);

    return (
        <Box
            sx={{
                border: nested ? 0 : "1px solid",
                borderColor: hasSelectedFilters ? "#f1c40f" : "divider",
                borderRadius: nested ? 1.5 : 2,
                bgcolor: hasSelectedFilters && !open
                    ? "rgba(241,196,15,0.12)"
                    : nested
                        ? (open ? "var(--projects-filter-nested-open-bg, rgba(14,43,77,0.055))" : "var(--projects-filter-nested-bg, rgba(14,43,77,0.035))")
                        : "var(--projects-filter-bg, #ffffff)",
                overflow: "hidden",
                boxShadow: hasSelectedFilters && !open ? "inset 3px 0 0 #f1c40f" : "none",
                transition: "background-color .2s ease, border-color .2s ease, box-shadow .2s ease"
            }}
        >
            <Box
                component="button"
                type="button"
                onClick={() => setOpen((current) => !current)}
                aria-expanded={open}
                sx={{
                    width: "100%",
                    px: nested ? 1 : 1.5,
                    py: nested ? 0.85 : 1.1,
                    border: 0,
                    cursor: "pointer",
                    fontWeight: 700,
                    color: nested ? "var(--projects-filter-muted-text, rgba(0,0,0,0.6))" : "var(--projects-filter-text, #0e2b4d)",
                    bgcolor: "transparent",
                    userSelect: "none",
                    fontSize: nested ? 12 : "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: nested ? 0.75 : 1,
                    textAlign: "left",
                    borderBottom: open && !nested ? "1px solid" : 0,
                    borderColor: hasSelectedFilters ? "#f1c40f" : "divider"
                }}
            >
                <Box
                    component={motion.span}
                    animate={{ rotate: open ? 90 : 0 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                    sx={{ display: "inline-flex", lineHeight: 1 }}
                >
                    {">"}
                </Box>
                <Box component="span">{title}</Box>
                {hasSelectedFilters && (
                    <Box
                        component="span"
                        sx={{
                            ml: "auto",
                            minWidth: 22,
                            height: 22,
                            px: 0.65,
                            borderRadius: 999,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f1c40f",
                            color: "#0e2b4d",
                            fontSize: 12,
                            fontWeight: 900,
                            lineHeight: 1
                        }}
                    >
                        {selectedCount}
                    </Box>
                )}
            </Box>

            <AnimatePresence initial={false}>
                {open && (
                    <Box
                        component={motion.div}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.24, ease: "easeOut" }}
                        sx={{ overflow: "hidden" }}
                    >
                        {children}
                    </Box>
                )}
            </AnimatePresence>
        </Box>
    );
}

function FilterGroup({ title, options, subgroups, selectedFilters, onToggle, selectedTone = "blue" }) {
    const selectedCount = options.filter((option) => selectedFilters.includes(option)).length;

    return (
        <Box
            component={motion.div}
            layout
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25, ease: "easeOut", layout: { duration: 0.24, ease: "easeOut" } }}
            sx={{
                overflow: "hidden"
            }}
        >
            <AnimatedDropdown title={title} selectedCount={selectedCount}>
            <Box
                component="summary"
                sx={{
                    display: "none",
                    px: 1.5,
                    py: 1.1,
                    cursor: "pointer",
                    fontWeight: 700,
                    color: "#0e2b4d",
                    userSelect: "none",
                    listStyle: "none",
                    "&::-webkit-details-marker": { display: "none" },
                    "&::before": {
                        content: '"▸"',
                        display: "inline-block",
                        mr: 1,
                        transition: "transform .2s ease"
                    },
                    "details[open] > &::before": {
                        transform: "rotate(90deg)"
                    }
                }}
            >
                {title}
            </Box>
            <Stack spacing={1} sx={{ p: 1 }}>
                {(subgroups || [{ title: null, options }]).map((group) => (
                    group.title ? (
                        <Box
                            key={group.title}
                            component={motion.div}
                            layout
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2, ease: "easeOut", layout: { duration: 0.22, ease: "easeOut" } }}
                            sx={{
                                overflow: "hidden"
                            }}
                        >
                            <AnimatedDropdown
                                title={group.title}
                                nested
                                selectedCount={group.options.filter((option) => selectedFilters.includes(option)).length}
                            >
                            <Box
                                component="summary"
                                sx={{
                                    display: "none",
                                    px: 1,
                                    py: 0.85,
                                    cursor: "pointer",
                                    userSelect: "none",
                                    color: "text.secondary",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    listStyle: "none",
                                    "&::-webkit-details-marker": { display: "none" },
                                    "&::before": {
                                        content: '"▸"',
                                        display: "inline-block",
                                        mr: 0.75,
                                        transition: "transform .2s ease"
                                    },
                                    "details[open] > &::before": {
                                        transform: "rotate(90deg)"
                                    }
                                }}
                            >
                                {group.title}
                            </Box>
                            <Stack
                                spacing={0.75}
                                component={motion.div}
                                layout
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.18, ease: "easeOut" }}
                                sx={{ px: 1.25, pb: 1 }}
                            >
                                {group.options.map((filter) => (
                                    <FilterOption
                                        key={filter}
                                        selected={selectedFilters.includes(filter)}
                                        onClick={() => onToggle(filter)}
                                        selectedTone={selectedTone}
                                    >
                                        {title === "Status" ? <StatusLabel status={filter} /> : filter}
                                    </FilterOption>
                                ))}
                            </Stack>
                            </AnimatedDropdown>
                        </Box>
                    ) : (
                        <Stack
                            key="filters"
                            spacing={0.75}
                            component={motion.div}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                        >
                            {group.options.map((filter) => (
                                <FilterOption
                                    key={filter}
                                    selected={selectedFilters.includes(filter)}
                                    onClick={() => onToggle(filter)}
                                    selectedTone={selectedTone}
                                >
                                    {title === "Status" ? <StatusLabel status={filter} /> : filter}
                                </FilterOption>
                            ))}
                        </Stack>
                    )
                ))}
            </Stack>
            </AnimatedDropdown>
        </Box>
    );
}

function getProjectStatusLabels(project) {
    const hasDemo = Boolean(project.links?.demo);
    const hasVideoOnly = !hasDemo && /video/i.test(project.description || "");

    return [
        hasDemo ? "Live" : null,
        hasVideoOnly ? "Video" : null,
        project.links?.repo ? "Code" : null
    ].filter(Boolean);
}

function ProjectStatusChips({ project }) {
    const hasDemo = Boolean(project.links?.demo);
    const hasVideoOnly = !hasDemo && /video/i.test(project.description || "");
    const availability = hasVideoOnly ? "Video" : hasDemo ? "Live" : null;

    return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {availability && (
                <Label tone={availability === "Live" ? "live" : "warning"}>
                    <StatusLabel status={availability} />
                </Label>
            )}
            {project.links?.repo && (
                <Label tone="code">
                    <StatusLabel status="Code" />
                </Label>
            )}
        </Stack>
    );
}

function ProjectCard({ project, featured = false }) {
    const mediaHeight = featured ? 250 : 200;
    const hasDemo = Boolean(project.links?.demo);
    const hasRepo = Boolean(project.links?.repo);
    const hasVideoOnly = !hasDemo && /video/i.test(project.description || "");
    const hasActions = hasDemo || hasRepo || hasVideoOnly;

    return (
        <Card
            className="project-card"
            component={motion.div}
            whileHover={{ y: -4 }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", borderRadius: 3 }}
        >
            <Box
                sx={{
                    position: "relative",
                    height: mediaHeight,
                    background: `linear-gradient(135deg, ${project.gradient[0]}, ${project.gradient[1]})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                    overflow: "hidden",
                }}
            >
                <LazyThumb src={project.thumb} alt={project.title} sx={{ height: mediaHeight }} />

                <LazyImage
                    src={project.cornerLogo}
                    alt={`${project.title} tech`}
                    sx={{
                        width: 36,
                        height: 36,
                        objectFit: "contain"
                    }}
                    wrapperSx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        boxShadow: 2,
                        bgcolor: "background.paper",
                        p: 0.5,
                        zIndex: 3
                    }}
                />
            </Box>

            <Box className="project-card-details">
                <CardContent className="project-card-content" sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography variant={featured ? "h5" : "h6"}>{project.title}</Typography>

                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {getProjectTechLabels(project).slice(0, 5).map((tech, index) => (
                            <Label key={index}>{tech}</Label>
                        ))}
                    </Stack>

                    <Box sx={{ mt: "auto" }}>
                        <ProjectStatusChips project={project} />
                    </Box>

                    <Typography variant="body2" sx={clamp}>
                        {project.description}
                    </Typography>
                </CardContent>

                <Stack
                    className="project-card-actions"
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{
                        px: 2,
                        py: 1.5,
                        bgcolor: "transparent"
                    }}
                >
                    {hasDemo && (
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<PlayCircle />}
                                component="a"
                            href={project.links.demo}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-visit-button"
                            sx={{ minWidth: 118 }}
                        >
                            Visit Site
                        </Button>
                    )}

                    {hasVideoOnly && (
                        <Button
                                size="small"
                                variant="contained"
                                startIcon={<PlayCircle />}
                                component={Link}
                                to={`/videos?video=${encodeURIComponent(project.videoTitle || project.title)}`}
                                state={{ videoTitle: project.videoTitle || project.title }}
                                sx={{
                                    minWidth: 118,
                                    bgcolor: "#f1c40f",
                                    color: "#0e2b4d",
                                    "&:hover": {
                                        bgcolor: "#d9ad0d",
                                        color: "#0e2b4d",
                                    }
                                }}
                            >
                                Play Video
                            </Button>
                    )}

                    {hasRepo && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<GitHub />}
                            component="a"
                                href={project.links.repo}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ minWidth: 118 }}
                            >
                                View Code
                            </Button>
                    )}

                    {!hasActions && (
                        <Button
                            size="small"
                            variant="outlined"
                            disabled
                            sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                            Currently unavailable
                        </Button>
                    )}
                </Stack>
            </Box>
        </Card>
    );
}

function Projects() {
    const [activeCategoryKey, setActiveCategoryKey] = useState(categoryPages[0].key);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(() =>
        typeof window !== "undefined" ? !window.matchMedia("(max-width: 899px)").matches : true
    );
    const [sidebarClosing, setSidebarClosing] = useState(false);
    const [isMobileSidebarViewport, setIsMobileSidebarViewport] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia("(max-width: 899px)").matches : false
    );
    const [showProjectsTopButton, setShowProjectsTopButton] = useState(false);
    const gridRef = useRef(null);
    const projectsScrollerRef = useRef(null);
    const sidebarCloseTimeoutRef = useRef(null);
    const sidebarVisible = sidebarOpen || sidebarClosing;

    const activeCategory = categoryPages.find((category) => category.key === activeCategoryKey) || categoryPages[0];
    const categoryItems = useMemo(
        () => activeCategory.key === "all"
            ? projects
            : projects.filter((project) => project.category === activeCategory.key),
        [activeCategory.key]
    );

    const pageItems = useMemo(
        () => categoryItems.filter((project) => {
            const projectFilters = new Set([
                ...getProjectTechLabels(project),
                ...getProjectStatusLabels(project)
            ]);
            return selectedFilters.every((filter) => projectFilters.has(filter));
        }),
        [categoryItems, selectedFilters]
    );

    const filterGroups = useMemo(() => {
        const technology = new Set();

        pageItems.forEach((project) => {
            getProjectTechLabels(project).forEach((tech) => technology.add(tech));
        });

        selectedFilters.forEach((filter) => {
            if (!statusFilterValues.includes(filter)) {
                technology.add(filter);
            }
        });

        const technologyOptions = Array.from(technology);
        return [
            { title: "Technology", options: technologyOptions, subgroups: getTechnologySubgroups(technologyOptions) },
            { title: "Status", options: statusFilterValues }
        ].filter((group) => group.options.length > 0);
    }, [pageItems, selectedFilters]);

    const isMobileViewport = () => window.matchMedia("(max-width: 899px)").matches;

    const scrollProjectViewToTop = () => {
        if (projectsScrollerRef.current) {
            projectsScrollerRef.current.scrollTop = 0;
        }

        const pageScroller = gridRef.current?.closest("main");
        if (pageScroller) {
            pageScroller.scrollTop = 0;
        }

        if (document.scrollingElement) {
            document.scrollingElement.scrollTop = 0;
        }

        window.scrollTo(0, 0);
    };

    const handleCategoryChange = (categoryKey) => {
        scrollProjectViewToTop();
        setActiveCategoryKey(categoryKey);
        setSelectedFilters([]);
    };

    useEffect(() => {
        const mobileQuery = window.matchMedia("(max-width: 899px)");
        const handleViewportChange = () => setIsMobileSidebarViewport(mobileQuery.matches);

        handleViewportChange();
        mobileQuery.addEventListener("change", handleViewportChange);

        return () => {
            mobileQuery.removeEventListener("change", handleViewportChange);
        };
    }, []);

    const openSidebar = () => {
        if (sidebarCloseTimeoutRef.current) {
            clearTimeout(sidebarCloseTimeoutRef.current);
        }

        setSidebarClosing(false);
        setSidebarOpen(true);
    };

    const closeSidebar = () => {
        if (!isMobileViewport()) {
            setSidebarOpen(false);
            return;
        }

        setSidebarClosing(true);
        sidebarCloseTimeoutRef.current = setTimeout(() => {
            setSidebarOpen(false);
            setSidebarClosing(false);
        }, 280);
    };

    const toggleSidebar = () => {
        if (sidebarOpen) {
            closeSidebar();
        } else {
            openSidebar();
        }
    };

    useEffect(() => {
        const projectsScroller = projectsScrollerRef.current;
        if (!projectsScroller) return;

        const handleScroll = () => {
            const maxScroll = projectsScroller.scrollHeight - projectsScroller.clientHeight;
            setShowProjectsTopButton(maxScroll > 0 && projectsScroller.scrollTop / maxScroll > 0.4);
        };

        handleScroll();
        projectsScroller.addEventListener("scroll", handleScroll);

        return () => {
            projectsScroller.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (sidebarCloseTimeoutRef.current) {
                clearTimeout(sidebarCloseTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setShowProjectsTopButton(false);
        scrollProjectViewToTop();
        requestAnimationFrame(() => {
            scrollProjectViewToTop();
        });
    }, [activeCategoryKey, selectedFilters]);

    const scrollProjectsToTop = () => {
        projectsScrollerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    };

    const toggleFilter = (filter) => {
        const isStatusFilter = statusFilterValues.includes(filter);
        scrollProjectViewToTop();

        setSelectedFilters((current) =>
            current.includes(filter)
                ? current.filter((item) => item !== filter)
                : isStatusFilter
                    ? [...current.filter((item) => !statusFilterValues.includes(item)), filter]
                    : [...current, filter]
        );
    };

    const containerVariants = {
        hidden: {},
        visible: {
            transition: { staggerChildren: 0.18, when: "beforeChildren" }
        },
        exit: {
            transition: { staggerChildren: 0.10, staggerDirection: -1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 16, scale: 0.98 },
        visible: {
            opacity: 1, y: 0, scale: 1,
            transition: { type: "spring", stiffness: 280, damping: 22 }
        },
        exit: { opacity: 0, y: -8, scale: 0.98, transition: { duration: 0.2 } }
    };

    return (
        <AnimatePresence mode="wait" m={0} p={0} >
            <Box
                id="projects"
                ref={gridRef}
                sx={{
                    px: { xs: 0, md: 0 },
                    py: { xs: 0, md: 0 },
                    boxSizing: "border-box",
                    height: "100%",
                    maxHeight: "100%",
                    minHeight: 0,
                    overflow: "hidden",
                    bgcolor: "rgba(14,43,77,0.035)"
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: sidebarOpen ? "274px minmax(0, 1fr)" : "56px minmax(0, 1fr)"
                        },
                        gap: { xs: 3, md: 2.5 },
                        alignItems: "stretch",
                        height: "100%",
                        maxHeight: "100%",
                        minHeight: 0,
                        overflow: "hidden",
                        transition: "grid-template-columns .25s ease"
                    }}
                >
                    {!sidebarOpen && !sidebarClosing && (
                        <Portal>
                        <Box
                            component="button"
                            type="button"
                            onClick={openSidebar}
                            aria-label="Open project filters"
                            sx={{
                                position: "fixed",
                                left: 0,
                                top: "17vh",
                                zIndex: 1400,
                                display: { xs: "inline-flex", md: "none" },
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                gap: 0.45,
                                width: 34,
                                height: 108,
                                p: 0,
                                border: "1px solid #f1c40f",
                                borderLeft: 0,
                                borderRadius: "0 14px 14px 0",
                                bgcolor: "#f1c40f",
                                color: "#0e2b4d",
                                boxShadow: "0 8px 24px rgba(14,43,77,0.18)",
                                cursor: "pointer",
                                fontWeight: 800,
                                fontSize: 12,
                                lineHeight: 1,
                                textTransform: "none",
                                "&:hover": {
                                    bgcolor: "#ffd84d"
                                },
                                "&:focus-visible": {
                                    outline: "2px solid #0e2b4d",
                                    outlineOffset: 2
                                }
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    writingMode: "vertical-rl",
                                    transform: "rotate(180deg)"
                                }}
                            >
                                Filters
                            </Box>
                            <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
                                {">"}
                            </Box>
                        </Box>
                        </Portal>
                    )}

                    <Portal disablePortal={!isMobileSidebarViewport || !sidebarVisible}>
                        <Box
                            component={motion.aside}
                            className={`projects-sidebar ${sidebarVisible ? "projects-sidebar-visible" : "projects-sidebar-collapsed"}`}
                            initial={{ opacity: 0, x: -24 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.35, ease: "easeOut" }}
                            onClick={() => {
                                if (!sidebarOpen) openSidebar();
                            }}
                            sx={{
                            p: sidebarVisible ? 2 : 0,
                            bgcolor: sidebarVisible ? "var(--projects-sidebar-bg, #ffffff)" : "#f1c40f",
                            border: "1px solid",
                            borderColor: sidebarVisible ? "var(--projects-sidebar-border, rgba(14,43,77,0.12))" : "#f1c40f",
                            borderLeft: { md: 0 },
                            borderRadius: sidebarVisible ? { xs: 0, md: "0 20px 20px 0" } : { xs: "0 18px 18px 0", md: "0 20px 20px 0" },
                            boxShadow: "var(--projects-sidebar-shadow, 0 8px 24px rgba(14,43,77,0.08))",
                            position: sidebarVisible ? { xs: "fixed", md: "relative" } : { xs: "fixed", md: "relative" },
                            inset: sidebarVisible ? { xs: 0, md: "auto" } : { xs: "17vh auto auto 0", md: "auto" },
                            transform: "none",
                            zIndex: sidebarVisible ? { xs: 1300, md: "auto" } : { xs: 1200, md: "auto" },
                            alignSelf: "stretch",
                            width: sidebarVisible ? { xs: "100%", md: "auto" } : { xs: 34, md: "auto" },
                            maxWidth: sidebarVisible ? { xs: "100vw", md: "none" } : { xs: 34, md: "none" },
                            boxSizing: "border-box",
                            height: sidebarVisible ? { xs: "100dvh", md: "100%" } : { xs: 108, md: "100%" },
                            maxHeight: sidebarVisible ? { xs: "100dvh", md: "100%" } : { xs: "none", md: "100%" },
                            overflowY: sidebarVisible ? { xs: "auto", md: "auto" } : { xs: "visible", md: "auto" },
                            overflowX: "clip",
                            overscrollBehavior: "contain",
                            transition: "padding .25s ease, background-color .25s ease, border-color .25s ease, border-radius .25s ease",
                            direction: { md: "rtl" },
                            cursor: sidebarVisible ? "default" : "pointer",
                            display: !sidebarVisible ? { xs: "none", md: "block" } : "block",
                            animation: sidebarClosing
                                ? { xs: "projectsMobileSidebarOut 0.28s ease-in forwards", md: "none" }
                                : sidebarOpen
                                    ? { xs: "projectsMobileSidebarIn 0.28s ease-out forwards", md: "none" }
                                    : "none"
                            }}
                        >
                        <Box sx={{ direction: "ltr", width: "100%", maxWidth: "100%", height: sidebarVisible ? "auto" : "100%", overflowX: "clip" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: sidebarVisible ? "flex-end" : "center",
                                    height: sidebarVisible ? "auto" : "100%",
                                    mb: sidebarVisible ? 1.5 : 0
                                }}
                            >
                            <Button
                                className="projects-sidebar-toggle"
                                type="button"
                                onClick={toggleSidebar}
                                aria-label={sidebarOpen ? "Collapse project filters" : "Expand project filters"}
                                sx={{
                                    minWidth: 0,
                                    width: sidebarVisible ? "auto" : { xs: "100%", md: 32 },
                                    height: sidebarVisible ? 32 : { xs: "100%", md: 32 },
                                    mb: 0,
                                    px: sidebarVisible ? 1 : { xs: 0.35, md: 0 },
                                    py: sidebarVisible ? 0 : { xs: 0.75, md: 0 },
                                    color: "#0e2b4d",
                                    bgcolor: "transparent",
                                    borderRadius: sidebarVisible ? 999 : { xs: "0 14px 14px 0", md: "50%" },
                                    textTransform: "none",
                                    position: "relative",
                                    flexDirection: !sidebarVisible ? { xs: "column", md: "row" } : "row",
                                    justifyContent: "center",
                                    fontWeight: 800,
                                    fontSize: sidebarVisible ? 13 : { xs: 12, md: 24 },
                                    lineHeight: 1,
                                    gap: !sidebarVisible ? { xs: 0.45, md: 0 } : 0,
                                    "&:hover": {
                                        bgcolor: sidebarVisible ? "rgba(14,43,77,0.08)" : "rgba(14,43,77,0.12)"
                                    }
                                }}
                            >
                                {sidebarVisible && <Box component="span" sx={{ mr: 0.75 }}>Close</Box>}
                                {!sidebarVisible && (
                                    <Box
                                        component="span"
                                        sx={{
                                            display: { xs: "inline-flex", md: "none" },
                                            writingMode: "vertical-rl",
                                            transform: "rotate(180deg)"
                                        }}
                                    >
                                        Filters
                                    </Box>
                                )}
                                <Box
                                    component="span"
                                    sx={{
                                        lineHeight: 1,
                                        fontSize: !sidebarVisible ? { xs: 18, md: 24 } : 24,
                                        position: "static"
                                    }}
                                >
                                    {sidebarVisible ? "<" : ">"}
                                </Box>
                            </Button>
                            </Box>

                            {sidebarVisible && (
                                <Box
                                    component={motion.div}
                                    initial={{ opacity: 0, x: -28 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.25, ease: "easeOut" }}
                                >
                                <Typography variant="overline" sx={{ px: 0.5, color: "text.secondary", fontWeight: 800, letterSpacing: 1 }}>
                                    Categories
                                </Typography>
                                <Stack spacing={0.35} sx={{ mt: 1 }}>
                                    {categoryPages.map((category, index) => (
                                        <Button
                                            className={`projects-category-button ${category.key === activeCategory.key ? "projects-category-button-active" : ""}`}
                                            key={category.key}
                                            component={motion.button}
                                            initial={{ opacity: 0, x: -8 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.2, delay: index * 0.04, ease: "easeOut" }}
                                            variant="text"
                                            onClick={() => handleCategoryChange(category.key)}
                                            sx={{
                                                position: "relative",
                                                justifyContent: "flex-start",
                                                textTransform: "none",
                                                fontWeight: 800,
                                                minHeight: 42,
                                                px: 1.5,
                                                color: category.key === activeCategory.key ? "var(--projects-category-active-text, #0e2b4d)" : "var(--projects-sidebar-text, currentColor)",
                                                bgcolor: category.key === activeCategory.key ? "var(--projects-category-active-bg, rgba(14,43,77,0.08))" : "transparent",
                                                borderRadius: 1.5,
                                                "&::before": {
                                                    content: '""',
                                                    position: "absolute",
                                                    left: 0,
                                                    top: 8,
                                                    bottom: 8,
                                                    width: 3,
                                                    borderRadius: 99,
                                                    bgcolor: category.key === activeCategory.key ? "var(--projects-category-active-marker, #0e2b4d)" : "transparent"
                                                },
                                                "&:hover": {
                                                    bgcolor: "var(--projects-sidebar-hover-bg, rgba(14,43,77,0.08))"
                                                }
                                            }}
                                        >
                                            {category.title}
                                        </Button>
                                    ))}
                                </Stack>

                                <Stack
                                    spacing={1.25}
                                    sx={{ mt: 3 }}
                                    component={motion.div}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.25, delay: 0.12, ease: "easeOut" }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                        <Typography variant="overline" sx={{ px: 0.5, color: "text.secondary", fontWeight: 800, letterSpacing: 1 }}>
                                            Filters
                                        </Typography>
                                        {selectedFilters.length > 0 && (
                                            <Button
                                                className="projects-clear-filters"
                                                type="button"
                                                size="small"
                                                variant="text"
                                                onClick={() => setSelectedFilters([])}
                                                sx={{
                                                    minWidth: "auto",
                                                    px: 0.5,
                                                    py: 0.25,
                                                    color: "#3b806b",
                                                    bgcolor: "transparent",
                                                    borderRadius: 1,
                                                    textTransform: "none",
                                                    fontWeight: 800,
                                                    fontSize: 12,
                                                    lineHeight: 1.2,
                                                    "&:hover": {
                                                        color: "#0e2b4d",
                                                        bgcolor: "rgba(59,128,107,0.08)"
                                                    }
                                                }}
                                            >
                                                Clear all
                                            </Button>
                                        )}
                                    </Box>
                                    {filterGroups.map((group) => (
                                        <FilterGroup
                                            key={group.title}
                                            title={group.title}
                                            options={group.options}
                                            subgroups={group.subgroups}
                                            selectedFilters={selectedFilters}
                                            onToggle={toggleFilter}
                                            selectedTone="yellow"
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                        </Box>
                        </Box>
                    </Portal>

                    <Box
                        sx={{
                            minWidth: 0,
                            minHeight: 0,
                            height: "100%",
                            maxHeight: "100%",
                            overflow: "hidden",
                            position: "relative"
                        }}
                    >
                        <Box
                            ref={projectsScrollerRef}
                            sx={{
                                height: "100%",
                                maxHeight: "100%",
                                py: { xs: 0, md: 3 },
                                pb: 0,
                                pr: 0,
                                overflowY: "auto",
                                overflowX: "hidden",
                                borderLeft: 0,
                                overscrollBehavior: "contain",
                                scrollbarGutter: "stable"
                            }}
                        >
                            <Box
                                key={`${activeCategory.key}-${selectedFilters.join("-")}`}
                                component={motion.div}
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        lg: "repeat(3, minmax(0, 1fr))"
                                    },
                                    gap: 3,
                                    p: { xs: 1, md: 1.5 },
                                    pb: { xs: 5, md: 1.5 },
                                    alignItems: "stretch"
                                }}
                            >
                                {pageItems.map((project) => (
                                    <Box
                                        key={project.id}
                                        component={motion.div}
                                        variants={itemVariants}
                                        layout
                                        sx={{ display: "flex", minWidth: 0 }}
                                    >
                                        <ProjectCard project={project} featured />
                                    </Box>
                                ))}

                                {pageItems.length === 0 && (
                                    <Box sx={{ p: 3, bgcolor: "background.paper", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                                        <Typography variant="body1">No projects match those filters.</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Box
                            aria-hidden="true"
                            sx={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: { xs: "block", md: "none" },
                                height: 34,
                                pointerEvents: "none",
                                background: "linear-gradient(to bottom, var(--projects-mobile-fade-start, rgba(247,247,247,0)), var(--projects-mobile-fade-end, rgba(247,247,247,0.98)))",
                                zIndex: 2
                            }}
                        />

                        {showProjectsTopButton && (
                            <button
                                type="button"
                                onClick={scrollProjectsToTop}
                                className="projects-back-to-top"
                                title="Go to top"
                                aria-label="Go to top of projects"
                            >
                                <BsChevronDoubleUp />
                            </button>
                        )}
                    </Box>
                </Box>
            </Box>
        </AnimatePresence>
    );
}

export default Projects;
